import { useMemo, useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import CarCard from './CarCard';
import api from '../../services/api';
import { getRecentlyViewedCars } from '../../services/recentlyViewedService';
import { applyRecommendations } from '../../utils/recommendationEngine';
import '../AllCss/SimilarCars.css';

function SimilarCars({ currentCar }) {
  const allCars = useSelector((state) => state.cars.items || []);
  const wishlistIds = useSelector((state) => state.wishlist.items);
  const { isAuthenticated, user } = useSelector((state) => state.auth);
  const [recentlyViewedCars, setRecentlyViewedCars] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);
  const [isAutoPlaying, setIsAutoPlaying] = useState(true);

  // Fetch recently viewed for recommendation engine
  useEffect(() => {
    const fetchRecentlyViewed = async () => {
      try {
        if (isAuthenticated && user?.id) {
          const response = await api.get('/recently-viewed', { params: { limit: 20 } });
          if (response.data.success && response.data.data?.recentlyViewed) {
            setRecentlyViewedCars(response.data.data.recentlyViewed);
            return;
          }
        }
        const local = getRecentlyViewedCars();
        setRecentlyViewedCars(local.map((item) => ({ ...item.car, viewedAt: item.viewedAt || new Date().toISOString() })));
      } catch {
        const local = getRecentlyViewedCars();
        setRecentlyViewedCars(local.map((item) => ({ ...item.car, viewedAt: item.viewedAt || new Date().toISOString() })));
      }
    };
    fetchRecentlyViewed();
  }, [isAuthenticated, user]);

  const similarCars = useMemo(() => {
    if (!currentCar || !allCars || allCars.length === 0) return [];

    const availableCars = allCars.filter(
      (car) =>
        car.id !== currentCar.id &&
        car.status === 'available' &&
        String(car.stockNumber) !== String(currentCar.stockNumber)
    );
    if (availableCars.length === 0) return [];

    const wishlistCars = wishlistIds
      .map((id) => allCars.find((c) => String(c.id) === String(id)))
      .filter(Boolean);
    const hasUserData = recentlyViewedCars.length > 0 || wishlistCars.length > 0;

    // Use recommendation engine when user has browsing history or wishlist
    if (hasUserData) {
      const recommended = applyRecommendations(availableCars, recentlyViewedCars, {
        wishlistCars,
        maxRecommendations: 10,
      });
      return recommended.slice(0, 10);
    }

    // Fallback: same make/model/price logic for guests or no history
    const currentMake = currentCar.make?.toLowerCase();
    const currentModel = currentCar.model?.toLowerCase();
    const currentPrice = parseFloat(currentCar.price) || 0;
    const priceRange = currentPrice * 0.2;
    const result = [];
    const usedCarIds = new Set();

    const sameMakeCars = availableCars
      .filter((car) => car.make?.toLowerCase() === currentMake && !usedCarIds.has(car.id))
      .map((car) => ({ car, priceDiff: Math.abs(currentPrice - (parseFloat(car.price) || 0)) }))
      .sort((a, b) => a.priceDiff - b.priceDiff)
      .slice(0, 3);
    sameMakeCars.forEach((item) => {
      result.push(item.car);
      usedCarIds.add(item.car.id);
    });

    const sameModelCars = availableCars
      .filter(
        (car) =>
          car.make?.toLowerCase() === currentMake &&
          car.model?.toLowerCase() === currentModel &&
          !usedCarIds.has(car.id)
      )
      .map((car) => ({ car, priceDiff: Math.abs(currentPrice - (parseFloat(car.price) || 0)) }))
      .sort((a, b) => a.priceDiff - b.priceDiff)
      .slice(0, 3);
    sameModelCars.forEach((item) => {
      result.push(item.car);
      usedCarIds.add(item.car.id);
    });

    const similarPriceCars = availableCars
      .filter((car) => {
        if (usedCarIds.has(car.id)) return false;
        const carPrice = parseFloat(car.price) || 0;
        return currentPrice > 0 && Math.abs(currentPrice - carPrice) <= priceRange;
      })
      .map((car) => ({ car, priceDiff: Math.abs(currentPrice - (parseFloat(car.price) || 0)) }))
      .sort((a, b) => a.priceDiff - b.priceDiff)
      .slice(0, 4);
    similarPriceCars.forEach((item) => {
      result.push(item.car);
      usedCarIds.add(item.car.id);
    });

    return result.slice(0, 10);
  }, [currentCar, allCars, wishlistIds, recentlyViewedCars]);

  // Number of cards to show at once
  const cardsPerView = 3;
  const totalSlides = Math.max(0, similarCars.length - cardsPerView + 1);

  // Auto-slide every 5 seconds
  useEffect(() => {
    if (!isAutoPlaying || totalSlides <= 1) return;

    const interval = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % totalSlides);
    }, 5000);

    return () => clearInterval(interval);
  }, [isAutoPlaying, totalSlides]);

  const goToPrevious = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev - 1 + totalSlides) % totalSlides);
  };

  const goToNext = () => {
    setIsAutoPlaying(false);
    setCurrentIndex((prev) => (prev + 1) % totalSlides);
  };

  const goToSlide = (index) => {
    setIsAutoPlaying(false);
    setCurrentIndex(index);
  };

  if (!currentCar || similarCars.length === 0) {
    return null;
  }

  return (
    <section className="similar-cars-section">
      <div className="similar-cars-header">
        <div className="similar-cars-title-section">
        <h2 className="similar-cars-title">You May Also Like</h2>
        <p className="similar-cars-subtitle">
            {recentlyViewedCars.length > 0 || wishlistIds.length > 0
              ? 'Personalized for you based on your browsing and saved cars'
              : 'Similar vehicles prioritized by make, model, and price range'}
          </p>
        </div>
        {totalSlides > 1 && (
          <div className="similar-cars-controls">
            <button 
              className="similar-cars-arrow similar-cars-arrow-left" 
              onClick={goToPrevious}
              disabled={totalSlides <= 1}
              aria-label="Previous"
            >
              <ChevronLeft size={24} />
            </button>
            <button 
              className="similar-cars-arrow similar-cars-arrow-right" 
              onClick={goToNext}
              disabled={totalSlides <= 1}
              aria-label="Next"
            >
              <ChevronRight size={24} />
            </button>
          </div>
        )}
      </div>

      <div className="similar-cars-container">
        <div 
          className="similar-cars-track"
          style={{
            transform: `translateX(-${currentIndex * (100 / cardsPerView)}%)`,
          }}
        >
        {similarCars.map((car) => (
            <div key={car.id} className="similar-cars-slide">
              <CarCard car={car} />
            </div>
        ))}
        </div>
      </div>

      {/* Indicators */}
      {totalSlides > 1 && (
        <div className="similar-cars-indicators">
          {Array.from({ length: totalSlides }).map((_, index) => (
            <button
              key={index}
              className={`similar-cars-indicator ${index === currentIndex ? 'active' : ''}`}
              onClick={() => goToSlide(index)}
              aria-label={`Go to slide ${index + 1}`}
            />
          ))}
        </div>
      )}
    </section>
  );
}

export default SimilarCars;

